import { AMOUNT_QUOTE_DEBOUNCE_MS } from "../../utils/amountQuote";
import { useEffect, useState } from "react";
import { requestPrivateFees } from "../../utils/relayer-client";
import { usesPrivateFees } from "../../joinsplit-v2/privateFees";
import { EVM_CONFIG } from "../../config";
import type { TokenInfo } from "../../config";
import { ethers } from "ethers";
import {
  privatePoolForToken,
  privateSpendSource,
} from "../../joinsplit-v2/privatePools";
import {
  displayPrivateFeePolicy,
  approvePrivateFeePolicy,
  subscribePrivateFeeRefresh,
} from "../../joinsplit-v2/privateFeeApproval";
import { privateFeePreviewRequest } from "../helpers/privateFeePreview";
import type { StoredJoinSplitNote } from "../../joinsplit/types";

export type FeeAction = "deposit" | "withdraw" | "p2p" | "swap" | "bridge";
export interface PrivateFeePolicy {
  pool: string;
  chainId: number;
  gasReimbursementWei: string;
  gasReimbursementAmount: string;
  gasReimbursementToken: string;
  feeCredit?: string;
  token?: string;
  decimals?: number;
  symbol?: string;
  contextKey?: string;
  rates: Record<FeeAction, { protocolBps: number; partnerBps: number }>;
}
export function usePrivateFeePolicy(
  account?: string | null,
  asset?: TokenInfo | null,
  context?: {
    action: "deposit" | "p2p" | "withdraw";
    amount: string;
    recipient: string;
    notes: StoredJoinSplitNote[];
  },
  paused = false,
) {
  const [loadedPolicy, setPolicy] = useState<{
    key: string;
    scope: string;
    policy: PrivateFeePolicy;
  } | null>(null);
  const token = asset?.address ?? ethers.ZeroAddress;
  const scope = JSON.stringify([
    account,
    EVM_CONFIG.CHAIN_ID,
    token,
    asset?.decimals,
    asset?.symbol,
  ]);
  let requestedAmount = 0n;
  try {
    requestedAmount = ethers.parseUnits(
      context?.amount || "0",
      asset?.decimals ?? 18,
    );
  } catch {
    /* Form validation owns invalid input. */
  }
  const pool = usesPrivateFees()
    ? context && context.action !== "deposit"
      ? privateSpendSource(context.notes, token, requestedAmount).pool
      : privatePoolForToken(token)
    : ethers.ZeroAddress;
  const preview = context
    ? privateFeePreviewRequest({
        ...context,
        token,
        pool,
        decimals: asset?.decimals ?? 18,
        chainId: EVM_CONFIG.CHAIN_ID,
      })
    : undefined;
  const key = JSON.stringify([
    account,
    token,
    asset?.decimals,
    asset?.symbol,
    context?.amount,
    preview,
  ]);
  const previewJson = JSON.stringify(preview);
  useEffect(() => {
    // The operation captured the accepted policy before starting. Stop timers
    // and in-flight refreshes without erasing that fee breakdown.
    if (paused) return;
    setPolicy(null);
    displayPrivateFeePolicy(null);
    approvePrivateFeePolicy(null);
    if (!account || !usesPrivateFees() || previewJson === "null") return;
    const stop = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let generation = 0;
    const refresh = async (fresh = false) => {
      clearTimeout(timer);
      const current = ++generation;
      let loaded = false;
      try {
        const next = await requestPrivateFees<PrivateFeePolicy>(
          "config",
          {
            token,
            ...(fresh ? { refresh: true } : {}),
            ...(previewJson ? { preview: JSON.parse(previewJson) } : {}),
          },
          { pool, signal: stop.signal },
        );
        next.token = token;
        next.decimals = asset?.decimals ?? 18;
        next.symbol = asset?.symbol ?? "ETH";
        next.contextKey = key;
        if (
          next.chainId !== EVM_CONFIG.CHAIN_ID ||
          next.pool.toLowerCase() !== pool.toLowerCase()
        )
          throw Error("Fee configuration mismatch");
        if (
          ethers.getAddress(next.gasReimbursementToken) !==
            ethers.getAddress(token) ||
          !/^[0-9]+$/.test(next.gasReimbursementAmount)
        )
          throw Error("Fee asset mismatch");
        if (!stop.signal.aborted && current === generation) {
          setPolicy({ key, scope, policy: next });
          displayPrivateFeePolicy(next);
        }
        loaded = true;
      } catch {
        /* Wallet authentication may still be completing; keep the form pending. */
      }
      if (!stop.signal.aborted && current === generation)
        timer = setTimeout(() => void refresh(), loaded ? 30000 : 1000);
    };
    const unsubscribe = subscribePrivateFeeRefresh((contextKey) => {
      if (contextKey !== key || stop.signal.aborted) return;
      setPolicy(null);
      displayPrivateFeePolicy(null);
      approvePrivateFeePolicy(null);
      void refresh(true);
    });
    // Typing an amount should not launch a simulation for every keystroke.
    timer = setTimeout(refresh, AMOUNT_QUOTE_DEBOUNCE_MS);
    return () => {
      stop.abort();
      clearTimeout(timer);
      unsubscribe();
    };
  }, [
    account,
    token,
    pool,
    asset?.decimals,
    asset?.symbol,
    previewJson,
    key,
    scope,
    paused,
  ]);
  return loadedPolicy?.scope === scope && (paused || loadedPolicy.key === key)
    ? loadedPolicy.policy
    : null;
}
